import type { FeatureFlagConfig, FeatureFlagEnv } from './types';

/** Application name reported to Unleash for metrics and targeting. */
const DEFAULT_APP_NAME = 'aecom-ai-frontend';

/** How often (seconds) the client polls the Unleash Frontend API for changes. */
const DEFAULT_REFRESH_INTERVAL_SECONDS = 15;

/** Returns a trimmed env value, or `undefined` when missing/blank. */
const readEnv = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * Builds the Unleash client configuration from `VITE_UNLEASH_*` variables.
 *
 * Returns `null` when the required variables (`VITE_UNLEASH_URL` and
 * `VITE_UNLEASH_CLIENT_KEY`) are absent, which signals that feature flags are
 * disabled. In that state no client is created and every flag resolves to
 * `false`, so the app runs unchanged.
 *
 * Pure: takes the environment as an argument so it can be tested without
 * stubbing `import.meta.env`.
 */
export const buildConfig = (env: FeatureFlagEnv): FeatureFlagConfig | null => {
  const url = readEnv(env.VITE_UNLEASH_URL);
  const clientKey = readEnv(env.VITE_UNLEASH_CLIENT_KEY);

  if (!url || !clientKey) {
    return null;
  }

  const refreshIntervalOverride = readEnv(env.VITE_UNLEASH_REFRESH_INTERVAL);
  const parsedRefreshInterval = refreshIntervalOverride
    ? Number(refreshIntervalOverride)
    : Number.NaN;

  const config: FeatureFlagConfig = {
    url,
    clientKey,
    appName: readEnv(env.VITE_UNLEASH_APP_NAME) ?? DEFAULT_APP_NAME,
    refreshInterval: Number.isFinite(parsedRefreshInterval)
      ? parsedRefreshInterval
      : DEFAULT_REFRESH_INTERVAL_SECONDS,
  };

  const environment = readEnv(env.VITE_UNLEASH_ENVIRONMENT);
  if (environment) {
    config.environment = environment;
  }

  return config;
};

/**
 * Parses `VITE_UNLEASH_FORCE_ENABLED` — a comma-separated list of flag names
 * to force on. Unknown names are filtered out by the factory, which is the
 * only thing that knows the registry.
 */
export const parseForceEnabled = (value: string | undefined): Record<string, boolean> => {
  const raw = readEnv(value);
  if (!raw) {
    return {};
  }
  return raw
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name !== '')
    .reduce<Record<string, boolean>>((forced, name) => {
      forced[name] = true;
      return forced;
    }, {});
};
